import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Search, Eye, X, Mail, Phone, Building2, Clock, Pencil, Trash2, GitMerge, AlertTriangle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminContacts, editContact, deleteContact, mergeContacts, findContactDuplicates } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ConfirmModal = ({ open, title, message, warning, onConfirm, onCancel, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white/60 text-sm mb-4">{message}</p>
        {warning && <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs mb-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{warning}</span></div>}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium ${danger ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30' : 'bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30'}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

export const AdminContacts = () => {
  const { t } = useApp();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [dupeLoading, setDupeLoading] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdminContacts();
      setContacts(res.data);
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const filtered = contacts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q)
      || (c.message || '').toLowerCase().includes(q);
  });

  const handleDelete = async (id) => {
    try {
      setActionLoading(true);
      await deleteContact(id);
      toast.success('Contact deleted');
      setConfirm(null);
      if (selected?.id === id) setSelected(null);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true);
      await editContact(selected.id, editForm);
      toast.success('Contact updated');
      setEditing(false);
      fetchContacts();
      // Update selected with new data
      setSelected(prev => ({ ...prev, ...editForm }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFindDuplicates = async (contact) => {
    try {
      setDupeLoading(true);
      const res = await findContactDuplicates(contact.id);
      setDuplicates(res.data);
      if (res.data.length === 0) toast.info('No duplicates found');
    } catch (err) {
      toast.error('Failed to find duplicates');
    } finally {
      setDupeLoading(false);
    }
  };

  const handleMerge = async (primaryId, secondaryId) => {
    try {
      setActionLoading(true);
      await mergeContacts({ primary_id: primaryId, secondary_ids: [secondaryId] });
      toast.success('Contacts merged');
      setDuplicates(null);
      setConfirm(null);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-contacts-page">
      <Toaster position="top-right" theme="dark" />

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="contacts-heading">
            <MessageSquare className="w-7 h-7 text-[#00D4FF]" />
            Contact Submissions
          </h1>
          <p className="text-white/40 text-sm mt-1">{filtered.length} submission{filtered.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input
          type="text"
          placeholder="Search by name, email, company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00D4FF]/50"
          data-testid="contacts-search"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20 text-white/40">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-white/40">No contact submissions found</div>
      ) : (
        <div className="overflow-x-auto border border-white/5">
          <table className="w-full" data-testid="contacts-table">
            <thead>
              <tr className="bg-white/5 border-b border-white/5">
                <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">Name</th>
                <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">Email</th>
                <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">Company</th>
                <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">Date</th>
                <th className="p-4 text-right text-xs font-semibold text-white/60 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  data-testid={`contact-row-${c.id}`}
                >
                  <td className="p-4 text-white font-medium">{c.name}</td>
                  <td className="p-4 text-white/70">{c.email}</td>
                  <td className="p-4 text-white/50">{c.company || '\u2014'}</td>
                  <td className="p-4 text-white/50 text-sm">{formatDate(c.created_at)}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setSelected(c); setEditing(false); setDuplicates(null); }} className="p-1.5 text-white/40 hover:text-[#00D4FF] transition-colors" data-testid={`view-contact-${c.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => setConfirm({ type: 'delete', id: c.id, title: 'Delete Contact', message: `Permanently delete contact submission from "${c.name}"?`, confirmLabel: 'Delete Forever', danger: true })} className="p-1.5 text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-contact-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setSelected(null); setEditing(false); setDuplicates(null); }}>
          <div
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-detail-modal"
          >
            <button
              onClick={() => { setSelected(null); setEditing(false); setDuplicates(null); }}
              className="absolute top-4 right-4 text-white/40 hover:text-white"
              data-testid="close-contact-modal"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-lg font-bold text-white mb-4">Contact Submission</h2>

            {/* Action buttons in modal */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="contact-actions">
              {!editing && (
                <button onClick={() => { setEditing(true); setEditForm({ name: selected.name, email: selected.email, company: selected.company || '', phone: selected.phone || '' }); }} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 text-white/70 text-xs hover:text-white" data-testid="btn-edit-contact">
                  <Pencil className="w-3.5 h-3.5" />Edit
                </button>
              )}
              <button onClick={() => handleFindDuplicates(selected)} disabled={dupeLoading} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs hover:bg-purple-500/20" data-testid="btn-find-contact-duplicates">
                <GitMerge className="w-3.5 h-3.5" />{dupeLoading ? 'Searching...' : 'Find Duplicates'}
              </button>
              <button onClick={() => setConfirm({ type: 'delete', id: selected.id, title: 'Delete Contact', message: `Permanently delete this submission from "${selected.name}"?`, confirmLabel: 'Delete Forever', danger: true })} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 text-xs hover:bg-red-500/20" data-testid="btn-delete-contact-modal">
                <Trash2 className="w-3.5 h-3.5" />Delete
              </button>
            </div>

            {/* Edit form */}
            {editing && (
              <div className="space-y-3 mb-4 p-4 bg-white/5 border border-white/10" data-testid="contact-edit-form">
                {['name', 'email', 'company', 'phone'].map(f => (
                  <div key={f}>
                    <label className="text-xs text-white/40 block mb-1">{f}</label>
                    <input value={editForm[f] || ''} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid={`edit-field-${f}`} />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button onClick={handleSaveEdit} disabled={actionLoading} className="px-3 py-1.5 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-xs font-medium" data-testid="btn-save-contact-edit">Save</button>
                  <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-white/60 text-xs">Cancel</button>
                </div>
              </div>
            )}

            {/* Duplicates panel */}
            {duplicates && duplicates.length > 0 && (
              <div className="mb-4 p-4 bg-purple-500/5 border border-purple-500/20" data-testid="contact-duplicates-panel">
                <h3 className="text-xs text-purple-400 uppercase font-semibold mb-2 flex items-center gap-1.5"><GitMerge className="w-3.5 h-3.5" />Potential Duplicates ({duplicates.length})</h3>
                <div className="space-y-2">
                  {duplicates.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2 bg-white/5 border border-white/5 text-xs">
                      <div>
                        <span className="text-white font-medium">{d.name}</span>
                        <span className="text-white/40 ml-2">{d.email}</span>
                      </div>
                      <button onClick={() => setConfirm({ type: 'merge', primaryId: selected.id, secondaryId: d.id, title: 'Merge Contacts', message: `Merge "${d.name}" into "${selected.name}"? The duplicate will be deleted and messages combined.`, confirmLabel: 'Merge' })} className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-400 hover:bg-purple-500/30" data-testid={`merge-contact-btn-${d.id}`}>
                        Merge
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact details */}
            {!editing && (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-[#00D4FF] mt-1 shrink-0" />
                  <div>
                    <p className="text-xs text-white/40 uppercase">Name</p>
                    <p className="text-white font-medium">{selected.name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#00D4FF] mt-1 shrink-0" />
                  <div>
                    <p className="text-xs text-white/40 uppercase">Email</p>
                    <a href={`mailto:${selected.email}`} className="text-[#00D4FF] hover:underline">{selected.email}</a>
                  </div>
                </div>

                {selected.company && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-white/40 mt-1 shrink-0" />
                    <div>
                      <p className="text-xs text-white/40 uppercase">Company</p>
                      <p className="text-white/80">{selected.company}</p>
                    </div>
                  </div>
                )}

                {selected.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-white/40 mt-1 shrink-0" />
                    <div>
                      <p className="text-xs text-white/40 uppercase">Phone</p>
                      <a href={`tel:${selected.phone}`} className="text-white/80 hover:text-[#00D4FF]">{selected.phone}</a>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-white/40 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs text-white/40 uppercase">Submitted</p>
                    <p className="text-white/60 text-sm">{formatDate(selected.created_at)}</p>
                  </div>
                </div>

                <div className="pt-2 border-t border-white/10">
                  <p className="text-xs text-white/40 uppercase mb-2">Message</p>
                  <p className="text-white/80 text-sm whitespace-pre-wrap bg-white/5 p-4 border border-white/5">{selected.message}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        warning={confirm?.warning}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'delete') handleDelete(confirm.id);
          else if (confirm?.type === 'merge') handleMerge(confirm.primaryId, confirm.secondaryId);
        }}
      />
    </div>
  );
};
